/* Shared constants & utilities for the cover editor. */

export const PANELS = [
  { id: "front", label: "Front cover" },
  { id: "spine", label: "Spine" },
  { id: "back", label: "Back cover" },
];

export const DISPLAY_SCALE = 60;
// Paperback bleed: 0.125" per edge. Hardcover bleed: 0.51" per edge (KDP
// 2026 spec — the extra wrap folds around the rigid case boards).
export const BLEED = 0.125;
export const HARDCOVER_BLEED = 0.51;
export const SAFE = 0.375;

// Hardcover spine wrap allowance — added to the paper-thickness × pages
// spine calculation to account for the board hinge wrap.
export const HARDCOVER_SPINE_WRAP = 0.06;

/* Returns the per-edge bleed for the binding type ("paperback" | "hardcover"). */
export const getBleed = (binding) =>
    binding === "hardcover" ? HARDCOVER_BLEED : BLEED;

/* Returns the KDP spine width (inches) for the given page count, paper
   multiplier, and binding type. */
export const getSpineWidth = (pages, multiplier, binding) => {
    const base = (parseInt(pages) || 0) * (multiplier || 0);
    const extra = binding === "hardcover" ? HARDCOVER_SPINE_WRAP : 0;
    return Math.round((base + extra) * 10000) / 10000;
};

export const FONT_FAMILIES = [
  "Outfit", "Inter", "Cormorant Garamond", "Playfair Display", "Lora",
  "Merriweather", "Poppins", "Montserrat", "Raleway", "Oswald",
  "Bebas Neue", "Roboto Slab", "Anton", "Cinzel", "Caveat",
  "Pacifico", "Lobster", "Crimson Pro", "EB Garamond", "Source Serif 4",
];

/* Class name helpers used across editor sub-panels. */
export const editorInput =
  "w-full px-2 py-1.5 rounded-md bg-[#FAFAF9] border border-[#D3D1C7] text-xs text-[#2C2C2A] focus:outline-none focus:border-[#534AB7]";
export const textBtnCls =
  "w-full px-3 py-2 rounded-md border border-[#D3D1C7] text-xs text-[#2C2C2A] hover:bg-[#F1EFE8] text-left transition-colors";
export const shapeBtnCls =
  "px-3 py-2 rounded-md border border-[#D3D1C7] text-[#2C2C2A] hover:bg-[#F1EFE8] transition-colors";

/* Internal canvas objects (guides, snap lines, the back-cover dim overlay)
   should never appear in the layers panel and should be filtered when
   needed. */
export const isInternalObj = (o) => {
  if (!o) return false;
  const n = o.name || "";
  return n.startsWith("_guide") || n.startsWith("_snap") || n === "_overlay";
};

/* Build a flat snapshot of a Fabric object's editable properties. We do NOT
   spread the Fabric object directly — it has circular references via .canvas
   that crash React. */
export function snapshotProps(obj) {
  if (!obj) return null;
  return {
    type: obj.type || "",
    name: obj.name || "",
    text: obj.text || "",
    fontFamily: obj.fontFamily || "Outfit",
    fontSize: obj.fontSize || 16,
    fontWeight: obj.fontWeight || "normal",
    fontStyle: obj.fontStyle || "normal",
    underline: obj.underline || false,
    textAlign: obj.textAlign || "left",
    charSpacing: obj.charSpacing || 0,
    fill: typeof obj.fill === "string" ? obj.fill : (obj.fill || "#000000"),
    stroke: obj.stroke || "",
    strokeWidth: obj.strokeWidth || 0,
    textBackgroundColor: obj.textBackgroundColor || "",
    backgroundColor: obj.backgroundColor || "",
    padding: obj.padding || 0,
    opacity: obj.opacity ?? 1,
    width: Math.round((obj.width || 0) * (obj.scaleX || 1)),
    height: Math.round((obj.height || 0) * (obj.scaleY || 1)),
    rawWidth: obj.width || 0,
    rawHeight: obj.height || 0,
    scaleX: obj.scaleX || 1,
    scaleY: obj.scaleY || 1,
    radius: obj.radius || 0,
    x1: obj.x1, y1: obj.y1, x2: obj.x2, y2: obj.y2,
    angle: obj.angle || 0,
    visible: obj.visible !== false,
    locked: !!obj.lockMovementX,
  };
}

export const timeAgo = (date) => {
  if (!date) return "just now";
  const sec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
};
